/**
 * Degraded Mode Hook
 *
 * Listens for fds-id unavailability and shows a non-intrusive toast notification.
 * Only shows once per session to avoid annoyance.
 */

import { useEffect, useState, useCallback } from 'react'
import { isDegradedModeNotified } from '../services/fds-id'

export interface DegradedModeState {
  /** Whether degraded mode is active (fds-id unavailable) */
  isDegraded: boolean
  /** Whether the notification should be shown */
  showNotification: boolean
  /** Dismiss the notification */
  dismissNotification: () => void
}

/**
 * Hook to track and display fds-id degraded mode status
 */
export function useDegradedMode(): DegradedModeState {
  const [isDegraded, setIsDegraded] = useState(false)
  const [showNotification, setShowNotification] = useState(false)

  const dismissNotification = useCallback(() => {
    setShowNotification(false)
  }, [])

  useEffect(() => {
    // Check if already notified this session
    if (isDegradedModeNotified()) {
      setIsDegraded(true)
      // Don't show notification if already notified
      return
    }

    // Listen for degraded mode event from siwe.ts
    const handleDegradedMode = () => {
      setIsDegraded(true)
      setShowNotification(true)

      // Auto-dismiss after 5 seconds
      setTimeout(() => {
        setShowNotification(false)
      }, 5000)
    }

    window.addEventListener('fairdrop:degraded-mode', handleDegradedMode)

    return () => {
      window.removeEventListener('fairdrop:degraded-mode', handleDegradedMode)
    }
  }, [])

  return {
    isDegraded,
    showNotification,
    dismissNotification,
  }
}

export default useDegradedMode
